import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { trackAddToCartEvent, trackRemoveFromCartEvent } from '@/hooks/useAnalytics';

export interface CartItem {
  id: string;
  productId: string;
  variantId?: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  vendorId: string;
  vendorName: string;
  variantName?: string;
  isAddonItem?: boolean;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  total: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('cart');
    if (!saved) return [];
    
    try {
      const parsed: CartItem[] = JSON.parse(saved);
      // Immediately clean corrupted items on initial load
      const cleaned = parsed.filter(item => {
        if (!item.vendorId) {
          console.warn('Removing cart item with missing vendorId on init:', item.name);
          return false;
        }
        return true;
      });
      // If we cleaned items, update localStorage immediately
      if (cleaned.length !== parsed.length) {
        localStorage.setItem('cart', JSON.stringify(cleaned));
      }
      return cleaned;
    } catch {
      return [];
    }
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const hasLoadedFromDbRef = useRef(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load cart from database when user logs in - only once per session
  useEffect(() => {
    const loadCartFromDb = async () => {
      if (!user) {
        setIsInitialized(true);
        return;
      }

      // Prevent multiple loads (e.g., on tab switch causing re-renders)
      if (hasLoadedFromDbRef.current) {
        return;
      }
      hasLoadedFromDbRef.current = true;

      try {
        const { data, error } = await supabase
          .from('carts')
          .select('items')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading cart:', error);
        }

        if (data?.items && Array.isArray(data.items)) {
          const dbItems = data.items as unknown as CartItem[];
          const localCart = localStorage.getItem('cart');
          const localItems: CartItem[] = localCart ? JSON.parse(localCart) : [];

          // Clean corrupted items (missing vendorId) from both sources
          const cleanItems = (items: CartItem[]) => items.filter(item => {
            if (!item.vendorId) {
              console.warn('Removing cart item with missing vendorId:', item.name);
              return false;
            }
            return true;
          });

          const cleanedDbItems = cleanItems(dbItems);
          const cleanedLocalItems = cleanItems(localItems);

          // Merge local cart with database cart - use MAX quantity, not sum
          if (cleanedLocalItems.length > 0 && cleanedDbItems.length > 0) {
            const mergedItems = [...cleanedDbItems];
            for (const localItem of cleanedLocalItems) {
              const existingIndex = mergedItems.findIndex(i => i.id === localItem.id);
              if (existingIndex >= 0) {
                // Use the higher quantity to avoid duplication
                mergedItems[existingIndex].quantity = Math.max(
                  mergedItems[existingIndex].quantity, 
                  localItem.quantity
                );
              } else {
                mergedItems.push(localItem);
              }
            }
            setItems(mergedItems);
          } else if (cleanedDbItems.length > 0) {
            setItems(cleanedDbItems);
          } else if (cleanedLocalItems.length > 0) {
            setItems(cleanedLocalItems);
          }
          // If only local items exist and they're clean, they're already in state
        }
      } catch (err) {
        console.error('Error loading cart from database:', err);
      } finally {
        setIsInitialized(true);
      }
    };

    loadCartFromDb();
  }, [user]);

  // Sync cart to localStorage and database
  const syncCart = useCallback(async (cartItems: CartItem[]) => {
    localStorage.setItem('cart', JSON.stringify(cartItems));

    if (!user) return;

    // Debounce database sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(async () => {
      try {
        const total = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

        // Check if cart exists first
        const { data: existingCart } = await supabase
          .from('carts')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (existingCart) {
          // Update existing cart
          await supabase
            .from('carts')
            .update({
              items: cartItems as unknown as any,
              total,
              last_updated_at: new Date().toISOString(),
            })
            .eq('user_id', user.id);
        } else {
          // Insert new cart
          await supabase
            .from('carts')
            .insert({
              user_id: user.id,
              items: cartItems as unknown as any,
              total,
              last_updated_at: new Date().toISOString(),
            });
        }
      } catch (err) {
        console.error('Error syncing cart:', err);
      }
    }, 500);
  }, [user]);

  // Sync whenever items change (after initialization)
  useEffect(() => {
    if (isInitialized) {
      syncCart(items);
    }
  }, [items, isInitialized, syncCart]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Track recent adds to prevent double-tap/click duplicates
  const recentAddsRef = useRef<Map<string, number>>(new Map());

  const addItem = (item: Omit<CartItem, 'id'>) => {
    const id = `${item.productId}-${item.variantId || 'default'}`;
    const now = Date.now();
    const lastAdd = recentAddsRef.current.get(id);
    
    // Prevent duplicate adds within 500ms (debounce rapid clicks/taps)
    if (lastAdd && now - lastAdd < 500) {
      console.log('Duplicate add prevented for', id);
      return;
    }
    recentAddsRef.current.set(id, now);
    
    // Clean up old entries periodically
    if (recentAddsRef.current.size > 50) {
      const cutoff = now - 5000;
      recentAddsRef.current.forEach((timestamp, key) => {
        if (timestamp < cutoff) recentAddsRef.current.delete(key);
      });
    }

    setItems(prev => {
      const existing = prev.find(i => i.id === id);
      if (existing) {
        return prev.map(i => 
          i.id === id ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      return [...prev, { ...item, id }];
    });

    // Track add to cart event
    trackAddToCartEvent({
      item_id: item.productId,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
      item_brand: item.vendorName,
      item_variant: item.variantName,
    });
  };

  const removeItem = (id: string) => {
    // Track removal before removing
    const itemToRemove = items.find(i => i.id === id);
    if (itemToRemove) {
      trackRemoveFromCartEvent({
        item_id: itemToRemove.productId,
        item_name: itemToRemove.name,
        price: itemToRemove.price,
        quantity: itemToRemove.quantity,
      });
    }
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
  };

  const clearCart = async () => {
    setItems([]);
    
    // Also clear from database
    if (user) {
      try {
        await supabase
          .from('carts')
          .delete()
          .eq('user_id', user.id);
      } catch (err) {
        console.error('Error clearing cart from database:', err);
      }
    }
  };

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, itemCount, total }}>
      {children}
    </CartContext.Provider>
  );
};
